import React, { useState, useEffect, useRef } from 'react';
import styles from './SearchFilter.module.css';

/**
 * Reusable search filter component with debouncing.
 *
 * The debounce effect holds `onChange` in a ref, so its identity does not
 * matter to callers: an inline arrow re-created on every parent render (a
 * socket-fed board, an interval tick) will NOT restart the pending timer.
 */
interface SearchFilterProps {
  /** Current search value */
  value?: string;
  /** Callback when search value changes. */
  onChange?: (value: string) => void;
  /** Placeholder text for input */
  placeholder?: string;
  /** Debounce delay in milliseconds */
  debounceMs?: number;
  /** Whether input is disabled */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /**
   * Accessible name for the input. Without it every instance announces only
   * as an unlabelled text field, so a screen-reader user can't tell which
   * panel's search they're in.
   */
  ariaLabel?: string;
}

const SearchFilter = ({
  value = '',
  onChange = () => {},
  placeholder = 'Search...',
  debounceMs = 500,
  disabled = false,
  className = '',
  ariaLabel,
}: SearchFilterProps) => {
  const [localValue, setLocalValue] = useState<string>(value);

  // Latest onChange, kept in a ref so the debounce effect below can leave it
  // out of its deps: an unstable onChange from a board that re-renders on
  // every socket event would otherwise clear the pending timer each render
  // and the query would never commit.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  // Update local value when prop value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounced onChange
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (localValue !== value) {
        onChangeRef.current(localValue);
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [localValue, debounceMs, value]);

  const handleClear = () => {
    setLocalValue('');
    onChange('');
  };

  return (
    <div className={`${styles['search-filter']} ${className}`}>
      <div className={styles['search-input-container']}>
        <input
          type="text"
          value={localValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setLocalValue(e.target.value)
          }
          placeholder={placeholder}
          disabled={disabled}
          aria-label={ariaLabel}
          className={styles['search-input']}
        />
        {localValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className={styles['search-clear-btn']}
            title="Clear search"
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};

export default SearchFilter;
